import { PLAYER_PUBLIC_SITE_ID } from './boot/playerLoading.js';
import {
  resolveAppDownloadTitle,
  resolveAppDownloadUrl,
  shouldShowAppDownloadPromo,
} from './boot/appDownload.js';
import {
  listActiveLiveChatServices,
  openLiveChatUrl,
  resolveLiveChatFabLogo,
  resolveLiveChatOptionIcon,
  shouldShowHomeLiveChatFab,
} from './boot/liveChat.js';
import { HEADER_ICON_SRC } from './assets/header-icons/index.js';
import { CATEGORY_ICON_SRC } from './assets/category-icons/index.js';
import { isUploadedCategoryImage, originalCategoryIconKey } from './category-nav.js';
import { TOOLBAR_ICON_SRC } from './assets/toolbar-icons/index.js';
import { NOTICE_ICON_SRC } from './assets/notice/index.js';
import { REF_MENU_CAT_ORDER, REF_MENU_CAT_LABELS } from './assets/menu-icons/index.js';
import { createPlayerI18n } from './i18n/language.js';

const API_BASE = `/api/site/${PLAYER_PUBLIC_SITE_ID}/v1`;

const RECENT_PLAY_LIMIT = 30;

export function selectFavouriteImages(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((row, idx) => {
      const image = String(row?.imageUrl || row?.url || row?.image || '').trim();
      return {
        id: String(row?.id || image || `fav-${idx}`),
        image,
      };
    })
    .filter((row) => row.image);
}

/** Thin stroke line-icons inspired by reference casino lobby language (original SVGs). */
export function createAlfa9Ui({ useState, useEffect, useRef, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  function Svg({ children, size = 22, className = 'a9-ico' }) {
    return jsx('svg', {
      className,
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg',
      'aria-hidden': 'true',
      children,
    });
  }

  const stroke = {
    stroke: 'currentColor',
    strokeWidth: '1.6',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  function ToolbarIcon({ src, size = 21, alt = '' }) {
    return jsx('img', {
      className: 'a9-toolbar-icon',
      src,
      alt,
      width: size,
      height: size,
      draggable: false,
      decoding: 'async',
    });
  }

  function IconToolbarHome({ size } = {}) {
    return jsx(ToolbarIcon, { src: TOOLBAR_ICON_SRC.home, size });
  }
  function IconToolbarPromo({ size } = {}) {
    return jsx(ToolbarIcon, { src: TOOLBAR_ICON_SRC.promotion, size });
  }
  function IconToolbarDeposit({ size } = {}) {
    return jsx(ToolbarIcon, { src: TOOLBAR_ICON_SRC.deposit, size });
  }
  function IconToolbarMine({ size } = {}) {
    return jsx(ToolbarIcon, { src: TOOLBAR_ICON_SRC.mine, size });
  }

  function IconHome({ size } = {}) {
    return jsx(Svg, {
      size,
      children: jsx('path', { d: 'M4 11 12 4l8 7v8.5a1 1 0 0 1-1 1h-4.5V14h-5v6.5H5a1 1 0 0 1-1-1V11Z', ...stroke }),
    });
  }

  function IconGames({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('rect', { x: '3.5', y: '3.5', width: '7', height: '7', rx: '1.2', ...stroke }, 'a'),
        jsx('rect', { x: '13.5', y: '3.5', width: '7', height: '7', rx: '1.2', ...stroke }, 'b'),
        jsx('rect', { x: '3.5', y: '13.5', width: '7', height: '7', rx: '1.2', ...stroke }, 'c'),
        jsx('rect', { x: '13.5', y: '13.5', width: '7', height: '7', rx: '1.2', ...stroke }, 'd'),
      ],
    });
  }

  function IconPromo({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('path', { d: 'M12 3.5 14.1 8.8 20 9.5 15.7 13.3 17 19 12 16.1 7 19 8.3 13.3 4 9.5 9.9 8.8 12 3.5Z', ...stroke }, 'a'),
      ],
    });
  }

  function IconWallet({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('path', { d: 'M3.5 8.5h14.2A2.3 2.3 0 0 1 20 10.8v7A2.3 2.3 0 0 1 17.7 20H4.8A1.3 1.3 0 0 1 3.5 18.7V8.5Z', ...stroke }, 'a'),
        jsx('path', { d: 'M3.5 8.5 5 5.4A1.6 1.6 0 0 1 6.4 4.5h9.4', ...stroke }, 'b'),
        jsx('circle', { cx: '16.4', cy: '14.4', r: '1.1', fill: 'currentColor' }, 'c'),
      ],
    });
  }

  function IconProfile({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '12', cy: '9', r: '3.2', ...stroke }, 'a'),
        jsx('path', { d: 'M5.2 19.2c1.5-3 3.7-4.5 6.8-4.5s5.3 1.5 6.8 4.5', ...stroke }, 'b'),
      ],
    });
  }

  function IconLogin({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('path', { d: 'M10 4.5H6.8A2.3 2.3 0 0 0 4.5 6.8v10.4A2.3 2.3 0 0 0 6.8 19.5H10', ...stroke }, 'a'),
        jsx('path', { d: 'M12 12h7.5M16.5 8.8 19.7 12l-3.2 3.2', ...stroke }, 'b'),
      ],
    });
  }

  function IconSignup({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '10', cy: '9', r: '3', ...stroke }, 'a'),
        jsx('path', { d: 'M4.5 19c1.2-2.6 3.1-3.9 5.5-3.9s4.3 1.3 5.5 3.9', ...stroke }, 'b'),
        jsx('path', { d: 'M17.5 7.5v5.5M14.8 10.2h5.5', ...stroke }, 'c'),
      ],
    });
  }

  function IconLang({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '12', cy: '12', r: '8', ...stroke }, 'a'),
        jsx('path', { d: 'M4.8 12h14.4M12 4.2c2.2 2.5 2.2 13.1 0 15.6M12 4.2c-2.2 2.5-2.2 13.1 0 15.6', ...stroke }, 'b'),
      ],
    });
  }

  function IconMegaphone({ size } = {}) {
    // Exact reference announcement asset (bdvegas index-announcement-icon.svg).
    // Intrinsic 20×15; optional size scales width while preserving aspect ratio.
    const w = typeof size === 'number' ? size : 20;
    const h = Math.round((w * 15) / 20);
    return jsx('img', {
      className: 'a9-notice-announce-icon',
      src: NOTICE_ICON_SRC.announcement,
      alt: '',
      width: w,
      height: h,
      draggable: false,
      decoding: 'async',
      'aria-hidden': 'true',
    });
  }

  function IconSearch({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '11', cy: '11', r: '5.5', ...stroke }, 'a'),
        jsx('path', { d: 'M15.8 15.8 20 20', ...stroke }, 'b'),
      ],
    });
  }

  function RefCatImg({ src, size = 40, alt = '' }) {
    return jsx('img', {
      className: 'a9-cat-ref-img',
      src,
      alt,
      width: size,
      height: size,
      draggable: false,
      decoding: 'async',
    });
  }

  function IconHot({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.hot, size }); }
  function IconSports({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.sports, size }); }
  function IconCasino({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.casino, size }); }
  function IconSlots({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.slots, size }); }
  function IconCrash({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.crash, size }); }
  function IconTable({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.table, size }); }
  function IconFishing({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.fishing, size }); }
  function IconArcade({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.arcade, size }); }
  function IconLottery({ size } = {}) { return jsx(RefCatImg, { src: CATEGORY_ICON_SRC.lottery, size }); }

  function IconCrown({ size } = {}) {
    return jsx(Svg, {
      size,
      children: jsx('path', { d: 'M4.5 17.5h15l-1.2-9.2L14 11.2 12 5.5 10 11.2 5.7 8.3 4.5 17.5Z', ...stroke }),
    });
  }

  function IconUsers({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '9', cy: '9', r: '2.8', ...stroke }, 'a'),
        jsx('path', { d: 'M4.2 18c1-2.4 2.7-3.6 4.8-3.6s3.8 1.2 4.8 3.6', ...stroke }, 'b'),
        jsx('circle', { cx: '16.2', cy: '9.4', r: '2.2', ...stroke }, 'c'),
        jsx('path', { d: 'M14.2 14.2c1.6-.2 3 .7 3.8 2.6', ...stroke }, 'd'),
      ],
    });
  }

  function IconMail({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('rect', { x: '3.5', y: '6', width: '17', height: '12', rx: '1.5', ...stroke }, 'a'),
        jsx('path', { d: 'M4.2 7.2 12 12.4 19.8 7.2', ...stroke }, 'b'),
      ],
    });
  }

  function IconHistory({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '12', cy: '12', r: '7.5', ...stroke }, 'a'),
        jsx('path', { d: 'M12 8v4.5l3 1.8', ...stroke }, 'b'),
      ],
    });
  }

  function IconTarget({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '12', cy: '12', r: '7.5', ...stroke }, 'a'),
        jsx('circle', { cx: '12', cy: '12', r: '4', ...stroke }, 'b'),
        jsx('circle', { cx: '12', cy: '12', r: '1.2', fill: 'currentColor' }, 'c'),
      ],
    });
  }

  function IconLock({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('rect', { x: '5.5', y: '10.5', width: '13', height: '9', rx: '1.5', ...stroke }, 'a'),
        jsx('path', { d: 'M8.5 10.5V8.2a3.5 3.5 0 0 1 7 0v2.3', ...stroke }, 'b'),
      ],
    });
  }

  function IconSettings({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('circle', { cx: '12', cy: '12', r: '3', ...stroke }, 'a'),
        jsx('path', {
          d: 'M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M5.8 5.8l1.6 1.6M16.6 16.6l1.6 1.6M18.2 5.8l-1.6 1.6M7.4 16.6l-1.6 1.6',
          ...stroke,
        }, 'b'),
      ],
    });
  }

  function IconLogout({ size } = {}) {
    return jsxs(Svg, {
      size,
      children: [
        jsx('path', { d: 'M14 4.5h3.2A2.3 2.3 0 0 1 19.5 6.8v10.4A2.3 2.3 0 0 1 17.2 19.5H14', ...stroke }, 'a'),
        jsx('path', { d: 'M12 12H4.5M7.7 8.8 4.5 12l3.2 3.2', ...stroke }, 'b'),
      ],
    });
  }

  function IconHandshake({ size } = {}) {
    return jsx(Svg, {
      size,
      children: jsx('path', {
        d: 'M8 13.5 10.2 11l2.1 2.1 2-2 2.2 2.2M4.5 12.2c1.4-1.4 3-1.7 4.3-.8l1.4 1.1M19.5 12.2c-1.4-1.4-3-1.7-4.3-.8l-1.4 1.1',
        ...stroke,
      }),
    });
  }

  function resolveCategoryKey(id = '', name = '') {
    return originalCategoryIconKey(id, name);
  }

  function CategoryIcon({ id, name, icon, size = 40 }) {
    const uploaded = String(icon || '').trim();
    const src = isUploadedCategoryImage(uploaded)
      ? uploaded
      : CATEGORY_ICON_SRC[resolveCategoryKey(id, name)];
    if (src) {
      return jsx('span', {
        className: 'a9-cat-ico a9-cat-ico--ref',
        children: jsx(RefCatImg, { src, size, alt: '' }),
      });
    }
    return jsx('span', {
      className: 'a9-cat-ico',
      children: jsx(IconGames, { size: Math.max(18, size - 12) }),
    });
  }

  function ProfileMenuIcon({ id, size = 18 }) {
    const map = {
      personal: IconProfile,
      vip: IconCrown,
      referral: IconUsers,
      messages: IconMail,
      history: IconHistory,
      turnover: IconTarget,
      security: IconLock,
      settings: IconSettings,
      logout: IconLogout,
      promotions: IconPromo,
      partnerships: IconHandshake,
    };
    const Comp = map[id] || IconGames;
    return jsx(Comp, { size });
  }

  function Alfa9LeftRail({
    isLoggedIn,
    onHome,
    onGames,
    onPromotions,
    onDeposit,
    onProfile,
    onLogin,
    depositEnabled = true,
    loginEnabled = true,
    homeActive = false,
    promoActive = false,
    depositActive = false,
    profileActive = false,
  }) {
    const { phrase } = usePlayerI18n();
    return jsxs('aside', {
      className: 'a9-rail',
      'aria-label': phrase('Desktop navigation'),
      children: [
        jsx('button', {
          type: 'button',
          className: `a9-rail-btn${homeActive ? ' active' : ''}`,
          onClick: onHome,
          'aria-label': phrase('Home'),
          title: phrase('Home'),
          'aria-current': homeActive ? 'page' : undefined,
          children: jsx(IconHome, { size: 20 }),
        }),
        jsx('button', { type: 'button', className: 'a9-rail-btn', onClick: onGames, 'aria-label': phrase('Games'), title: phrase('Games'), children: jsx(IconGames, { size: 20 }) }),
        jsx('button', {
          type: 'button',
          className: `a9-rail-btn${promoActive ? ' active' : ''}`,
          onClick: onPromotions,
          'aria-label': phrase('Promotions'),
          title: phrase('Promotions'),
          'aria-current': promoActive ? 'page' : undefined,
          children: jsx(IconPromo, { size: 20 }),
        }),
        isLoggedIn
          ? jsxs('div', {
              className: 'a9-rail-group',
              children: [
                jsx('button', {
                  type: 'button',
                  className: `a9-rail-btn${depositActive ? ' active' : ''}`,
                  onClick: onDeposit,
                  disabled: !depositEnabled,
                  'aria-label': phrase('Deposit'),
                  title: phrase('Deposit'),
                  'aria-current': depositActive ? 'page' : undefined,
                  children: jsx(IconWallet, { size: 20 }),
                }),
                jsx('button', {
                  type: 'button',
                  className: `a9-rail-btn${profileActive ? ' active' : ''}`,
                  onClick: onProfile,
                  'aria-label': phrase('Profile'),
                  title: phrase('Profile'),
                  'aria-current': profileActive ? 'page' : undefined,
                  children: jsx(IconProfile, { size: 20 }),
                }),
              ],
            })
          : jsx('button', {
              type: 'button',
              className: 'a9-rail-btn',
              onClick: onLogin,
              disabled: !loginEnabled,
              'aria-label': phrase('Login'),
              title: phrase('Login'),
              children: jsx(IconLogin, { size: 20 }),
            }),
      ],
    });
  }

  function Alfa9HomePromo({ onOpenPromotions }) {
    const { phrase } = usePlayerI18n();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
      let cancelled = false;
      setError(false);
      fetch(`${API_BASE}/public/promotions`)
        .then(async (res) => {
          if (!res.ok) throw new Error('promo_http');
          return res.json();
        })
        .then((payload) => {
          if (cancelled) return;
          const list = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.promotions)
              ? payload.promotions
              : Array.isArray(payload)
                ? payload
                : [];
          setItems(list.filter(Boolean).slice(0, 8));
        })
        .catch(() => {
          if (!cancelled) {
            setItems([]);
            setError(true);
          }
        });
      return () => {
        cancelled = true;
      };
    }, []);

    return jsxs('section', {
      className: 'a9-home-promo',
      'aria-label': phrase('Promotions'),
      children: [
        jsxs('div', {
          className: 'a9-home-promo-head',
          children: [
            jsx('h2', { className: 'a9-section-title', children: phrase('Promotions') }),
            jsx('button', { type: 'button', className: 'a9-link-btn', onClick: onOpenPromotions, children: phrase('View all') }),
          ],
        }),
        items === null
          ? jsx('div', { className: 'a9-home-promo-empty', children: phrase('Loading promotions…') })
          : items.length === 0
            ? jsx('div', {
                className: 'a9-home-promo-empty',
                children: error ? phrase('Promotions unavailable right now.') : phrase('No promotions available.'),
              })
            : jsx('div', {
                className: 'a9-home-promo-track',
                children: items.map((item, idx) => {
                  const key = item.id || item._id || item.code || `promo-${idx}`;
                  const title = item.title || item.name || item.code || 'Promotion';
                  const image = item.imageUrl || item.bannerUrl || item.image || '';
                  const badge = item.badge || item.label || '';
                  return jsxs(
                    'button',
                    {
                      type: 'button',
                      className: 'a9-promo-card',
                      onClick: onOpenPromotions,
                      children: [
                        image
                          ? jsx('img', { src: image, alt: '', className: 'a9-promo-card-img', loading: 'lazy', decoding: 'async' })
                          : jsx('div', { className: 'a9-promo-card-fallback', 'aria-hidden': 'true' }),
                        jsxs('div', {
                          className: 'a9-promo-card-meta',
                          children: [
                            badge ? jsx('span', { className: 'a9-promo-badge', children: badge }) : null,
                            jsx('span', { className: 'a9-promo-title', children: title }),
                          ],
                        }),
                      ],
                    },
                    key
                  );
                }),
              }),
      ],
    });
  }

  /** Idle-hidden horizontal row; thin scrollbar ~1s while the user swipes/drags. */
  function HScrollRow({ className, children }) {
    const rowRef = useRef(null);
    const dragRef = useRef({ dragging: false, startX: null, scrollLeft: 0 });
    const hideTimerRef = useRef(0);

    const flashScrollbar = () => {
      const el = rowRef.current;
      if (!el) return;
      el.classList.add('is-scrolling');
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        el.classList.remove('is-scrolling');
      }, 1000);
    };

    useEffect(() => {
      const el = rowRef.current;
      if (!el) return undefined;
      const onScroll = () => flashScrollbar();
      el.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        el.removeEventListener('scroll', onScroll);
        clearTimeout(hideTimerRef.current);
      };
    }, []);

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse' || e.button === 0) {
        dragRef.current = {
          dragging: false,
          startX: e.clientX,
          scrollLeft: rowRef.current?.scrollLeft ?? 0,
        };
      }
    };
    const onPointerMove = (e) => {
      const el = rowRef.current;
      const drag = dragRef.current;
      if (!el || drag.startX === null) return;
      const dx = e.clientX - drag.startX;
      if (!drag.dragging && Math.abs(dx) > 10) drag.dragging = true;
      if (drag.dragging) el.scrollLeft = drag.scrollLeft - dx;
    };
    const onPointerUp = () => {
      const wasDragging = dragRef.current.dragging;
      dragRef.current.startX = null;
      if (wasDragging) {
        setTimeout(() => {
          dragRef.current.dragging = false;
        }, 80);
      } else {
        dragRef.current.dragging = false;
      }
    };
    const onClickCapture = (e) => {
      if (dragRef.current.dragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    return jsx('div', {
      className,
      ref: rowRef,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerLeave: onPointerUp,
      onClickCapture,
      children,
    });
  }

  /** Keep the public category API order, names, icons, and enabled rows. */
  function ensureRefGuestCategories(list = []) {
    return (Array.isArray(list) ? list : [])
      .filter((row) => row && row.enabled !== false)
      .map((row) => ({ ...row, name: String(row.name || '').trim() }));
  }

  function GuestHomeExtras({ favouriteImages = [], recentGames = [], onOpenPromotions, onOpenGame } = {}) {
    const { phrase } = usePlayerI18n();
    const favItems = selectFavouriteImages(favouriteImages);

    // UI-ready Recent Play list. No invented activity — only caller-supplied games.
    const recentItems = (Array.isArray(recentGames) ? recentGames : [])
      .filter((g) => g && (g.id || g.name || g.gameId))
      .slice(0, RECENT_PLAY_LIMIT);

    return jsxs('div', {
      className: 'a9-guest-home-extras',
      children: [
        jsxs('section', {
          className: 'a9-guest-favourites',
          'aria-label': phrase('Favourites'),
          children: [
            jsx('h2', { className: 'title a9-section-title', children: phrase('Favourites') }),
            favItems.length
              ? jsx(HScrollRow, {
                  className: 'a9-guest-fav-track',
                  children: favItems.map((item) =>
                    jsx(
                      'button',
                      {
                        type: 'button',
                        className: 'a9-guest-fav-card',
                        onClick: onOpenPromotions,
                        'aria-label': phrase('Favourite'),
                        children: jsx('img', {
                          src: item.image,
                          alt: '',
                          className: 'a9-guest-fav-img',
                          loading: 'lazy',
                          decoding: 'async',
                        }),
                      },
                      item.id
                    )
                  ),
                })
              : null,
          ],
        }),
        jsxs('section', {
          className: 'a9-guest-recent-play',
          'aria-label': phrase('Recent Play'),
          children: [
            jsx('h2', { className: 'title a9-section-title', children: phrase('Recent Play') }),
            jsx(HScrollRow, {
              className: 'a9-guest-recent-track',
              children: recentItems.length
                ? recentItems.map((game, idx) => {
                    const key = String(game.id || game.gameId || game.name || idx);
                    const name = game.name || game.title || phrase('Game');
                    const image = game.imageUrl || game.image || game.thumb || '';
                    return jsxs(
                      'button',
                      {
                        type: 'button',
                        className: 'a9-guest-recent-card game',
                        onClick: () => {
                          if (typeof onOpenGame === 'function') onOpenGame(game);
                        },
                        children: [
                          jsxs('div', {
                            className: 'a9-guest-recent-img game-img',
                            children: [
                              image
                                ? jsx('img', {
                                    src: image,
                                    alt: '',
                                    loading: 'lazy',
                                    decoding: 'async',
                                  })
                                : null,
                            ],
                          }),
                          jsx('div', { className: 'a9-guest-recent-name game-name', children: name }),
                        ],
                      },
                      key
                    );
                  })
                : null,
            }),
          ],
        }),
      ],
    });
  }

  function Alfa9AppDownloadPromo({ appDownload, isLoggedIn } = {}) {
    const { phrase } = usePlayerI18n();
    const [dismissed, setDismissed] = useState(false);

    if (isLoggedIn || dismissed || !shouldShowAppDownloadPromo(appDownload)) return null;

    const href = resolveAppDownloadUrl(appDownload);
    const title = resolveAppDownloadTitle(appDownload);
    const logo = String(appDownload?.appLogo || '').trim();

    const dismiss = () => {
      setDismissed(true);
    };

    return jsxs('aside', {
      className: 'a9-app-download-promo',
      'aria-label': title,
      children: [
        jsx('button', {
          type: 'button',
          className: 'a9-app-download-promo-close',
          onClick: dismiss,
          'aria-label': phrase('Close'),
          children: '×',
        }),
        jsx('span', { className: 'a9-app-download-promo-hot', children: phrase('HOT!') }),
        logo
          ? jsx('img', {
              className: 'a9-app-download-promo-logo',
              src: logo,
              alt: '',
              width: 44,
              height: 44,
            })
          : null,
        jsxs('div', {
          className: 'a9-app-download-promo-copy',
          children: [
            jsx('div', { className: 'a9-app-download-promo-title', children: title }),
            jsx('div', {
              className: 'a9-app-download-promo-stars',
              'aria-hidden': 'true',
              children: '★★★★★',
            }),
          ],
        }),
        jsx('a', {
          className: 'a9-app-download-promo-btn',
          href,
          children: phrase('Download'),
        }),
      ],
    });
  }

  function Alfa9HomeLiveChatFab({ pathname, branding } = {}) {
    const { phrase } = usePlayerI18n();
    const liveChat = branding?.liveChat || {};
    const services = listActiveLiveChatServices(liveChat);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const fallbackLogo = HEADER_ICON_SRC.customer;
    const hideOnMobile = liveChat.showOnMobile === false;

    useEffect(() => {
      setOpen(false);
    }, [pathname]);

    useEffect(() => {
      if (!open) return undefined;
      const onPointer = (event) => {
        if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
      };
      const onKey = (event) => {
        if (event.key === 'Escape') setOpen(false);
      };
      document.addEventListener('pointerdown', onPointer);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('pointerdown', onPointer);
        document.removeEventListener('keydown', onKey);
      };
    }, [open]);

    if (!shouldShowHomeLiveChatFab({ pathname, liveChat })) return null;

    const logo = resolveLiveChatFabLogo(branding, fallbackLogo);
    const openOne = (url) => {
      openLiveChatUrl(url);
      setOpen(false);
    };
    const onFabClick = () => {
      if (services.length === 1) {
        openOne(services[0].url);
        return;
      }
      setOpen((value) => !value);
    };

    return jsxs('div', {
      className:
        'a9-livechat-fab' +
        (open ? ' is-open' : '') +
        (hideOnMobile ? ' is-hide-mobile' : ''),
      ref: wrapRef,
      children: [
        services.length > 1 && open
          ? jsx('div', {
              className: 'a9-livechat-fab-menu',
              id: 'a9-livechat-fab-menu',
              role: 'menu',
              children: services.map((row) =>
                jsxs(
                  'button',
                  {
                    type: 'button',
                    role: 'menuitem',
                    className: 'a9-livechat-fab-option',
                    onClick: () => openOne(row.url),
                    children: [
                      jsx('img', {
                        className: 'a9-livechat-fab-option-logo',
                        src: resolveLiveChatOptionIcon(row, branding, fallbackLogo),
                        alt: '',
                        width: 36,
                        height: 36,
                        draggable: false,
                      }),
                      jsx('span', {
                        className: 'a9-livechat-fab-option-label',
                        children: phrase(row.label),
                      }),
                    ],
                  },
                  row.id,
                ),
              ),
            })
          : null,
        jsx('button', {
          type: 'button',
          className: 'a9-livechat-fab-btn',
          onClick: onFabClick,
          'aria-label': phrase('Live Chat'),
          'aria-haspopup': services.length > 1 ? 'menu' : undefined,
          'aria-expanded': services.length > 1 ? open : undefined,
          'aria-controls': services.length > 1 ? 'a9-livechat-fab-menu' : undefined,
          children: jsx('img', {
            className: 'a9-livechat-fab-logo',
            src: logo,
            alt: '',
            width: 56,
            height: 56,
            draggable: false,
          }),
        }),
      ],
    });
  }

  return {
    Alfa9LeftRail,
    Alfa9HomePromo,
    Alfa9AppDownloadPromo,
    Alfa9HomeLiveChatFab,
    GuestHomeExtras,
    ensureRefGuestCategories,
    CategoryIcon,
    resolveCategoryKey,
    ProfileMenuIcon,
    IconHome,
    IconToolbarHome,
    IconToolbarPromo,
    IconToolbarDeposit,
    IconToolbarMine,
    IconGames,
    IconPromo,
    IconWallet,
    IconProfile,
    IconLogin,
    IconSignup,
    IconLang,
    IconMegaphone,
    IconSearch,
    IconHot,
    IconSports,
    IconCasino,
    IconSlots,
    IconCrash,
    IconTable,
    IconFishing,
    IconArcade,
    IconLottery,
    IconCrown,
    IconUsers,
    IconMail,
    IconHistory,
    IconTarget,
    IconLock,
    IconSettings,
    IconLogout,
    IconHandshake,
  };
}
